import { useFormik } from "formik";
import _ from "lodash";
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import * as yup from "yup";

import { useTranslation } from "react-i18next";
import { useHistory, useLocation, useParams } from "react-router";
import { PDCAStages } from "../../config/Config";
import { useNotification } from "../../context/notification";
import GetSessionData from "../../services/getSessionData";
import { apiRequest } from "../../utils/api";
import { getLocalValue } from "../../utils/auth";
import { specialCharacterRegex } from "../../utils/customValidation";
import safeParse from "../../utils/safeParse";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import styles from "./SessionsConversationalForm.module.scss";

import { KeyboardArrowLeft, KeyboardArrowRight } from "@material-ui/icons";
import AiPopover from "../../components/AiPopover";
import Button from "../../components/Button";
import EditableQuestionAnswerForm from "../../components/EditableQuestionAnswerForm";
import NotesSidebar from "../../components/NotesSidebar";
import PageContent from "../../components/PageContent";
import Section from "../../components/Section";
import SectionTitle from "../../components/SectionTitle";
import SessionReminderCard from "../../components/SessionReminderCard";
import Stepper from "../../components/Stepper";
import SubjectMetricTargetInput from "../../components/SubjectMetricTargetInput";
import { DashboardLayout } from "../../layouts/DashboardLayout";
import { useCampaign } from "../../context/campaign";
import useNudgePopover from "../../hooks/useNudgePopover";
import usePiiGuard from "../../hooks/usePiiGuard";
import useCampaignCodes from "../../hooks/useCampaignCodes";
import useClientCompanyNames from "../../hooks/useClientCompanyNames";
import useSubFunctionalUnitNames from "../../hooks/useSubFunctionalUnitNames";
import AiBanner from "../../components/AiBanner";

dayjs.extend(utc);

// ---------- helpers ----------

// build the questions/answers config once
const QUESTION_COUNT = 7;
const questionFields = Array.from({ length: QUESTION_COUNT * 2 }, (_, i) => {
  const isQuestion = i % 2 === 0;
  const index = Math.floor(i / 2) + 1;
  return {
    name: `${isQuestion ? "question" : "answer"}${index}`,
  };
});

const questionAnswerPairs = Array.from({ length: QUESTION_COUNT }, (_, i) => ({
  question: questionFields[i * 2],
  answer: questionFields[i * 2 + 1],
}));

// Only the answer fields are scanned - the question fields are pre-filled,
// coach-authored prompts, not coachee-entered free text (out of scope for PII scanning).
const GUARDED_FIELDS = questionAnswerPairs.map(pair => pair.answer.name);

const conversationalSessionField = () =>
  yup
    .string()
    .required("Let's not skip this, it's important")
    .matches(specialCharacterRegex, "Special characters are not allowed");

const validationSchema = yup.object().shape({
  // Q/A fields
  ...questionFields.reduce((acc, { name }) => {
    acc[name] = conversationalSessionField();
    return acc;
  }, {}),
  // subjects
  subjects: yup
    .array()
    .of(
      yup.object().shape({
        reality_target: yup
          .number()
          .required("Please enter Target")
          .typeError("Target must be a number"),
      })
    )
    .required("Subjects are mandatory"),
});

const useQuery = () => new URLSearchParams(useLocation().search);

function useDebouncedCallback(fn, delay) {
  const debouncedFn = useMemo(() => _.debounce(fn, delay), [fn, delay]);

  // Stops the debounced function from being called when the component unmounts
  useEffect(() => {
    return () => {
      debouncedFn.cancel();
    };
  }, [debouncedFn]);

  return debouncedFn;
}

// ---------- component ----------

export function SessionsConversationalForm() {
  const history = useHistory();
  const { id } = useParams();
  const query = useQuery();
  const { t } = useTranslation();
  const { addNotification } = useNotification();

  const [sessionDetails, setSessionDetails] = useState({});
  const [sessionData, setSessionData] = useState({});
  const [growFormDetails, setGrowFormDetails] = useState(null);
  const [sessionReviewDate, setSessionReviewDate] = useState("");
  const [sessionDuration, setSessionDuration] = useState(0);
  const [questionNo, setQuestionNo] = useState(1);
  // AI evaluation badge per question, keyed by answer field name.
  const [qaFeedbackStates, setQaFeedbackStates] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isMetricEntered, setIsMetricEntered] = useState(false);
  const queryWantsEdit = query.get("edit") === "true";
  const [isEditMode, setIsEditMode] = useState(queryWantsEdit);

  const [subjectList, setSubjectList] = useState([]);

  const getMainSubjectId = () => {
    return subjectList?.[0]?.subject_id || null;
  };

  // Snapshot of the answers entered so far, sent with every nudge so the AI has
  // the full session context. Kept in a ref so the getter stays
  // stable and reads the latest values at nudge time.
  const sessionAnswersRef = useRef({});
  const getSessionAnswers = useCallback(() => sessionAnswersRef.current, []);

  const {
    popoverAnchorEl: kpiPopoverAnchorEl,
    streamedText: kpiStreamedText,
    nudgeType: kpiNudgeType,
    streaming: kpiStreaming,
    error: kpiError,
    isOpen: kpiIsOpen,
    aiDisabled: kpiAiDisabled,
    manuallyDisabled: kpiManuallyDisabled,
    handleNudgeRequest: handleKpiNudgeRequest,
    handleClosePopover: handleKpiClosePopover,
    handleFeedback: handleKpiFeedback,
    handleRefresh: handleKpiRefresh,
    handleDisableAI: handleKpiDisableAI,
    handleEnableAI: handleKpiEnableAI,
  } = useNudgePopover(id, getMainSubjectId);

  const {
    popoverAnchorEl: qaPopoverAnchorEl,
    streamedText: qaStreamedText,
    nudgeType: qaNudgeType,
    streaming: qaStreaming,
    error: qaError,
    isOpen: qaIsOpen,
    aiDisabled: qaAiDisabled,
    manuallyDisabled: qaManuallyDisabled,
    handleNudgeRequest: handleQaNudgeRequest,
    handleClosePopover: handleQaClosePopover,
    handleFeedback: handleQaFeedback,
    handleRefresh: handleQaRefresh,
    handleDisableAI: handleQaDisableAI,
    handleEnableAI: handleQaEnableAI,
    clearNudgeState: handleQaClearNudgeState,
  } = useNudgePopover(id, getMainSubjectId, undefined, getSessionAnswers);

  const campaignCodes = useCampaignCodes();
  const clientCompanyNames = useClientCompanyNames();
  const subFunctionalUnitNames = useSubFunctionalUnitNames();
  const piiGuard = usePiiGuard(
    id,
    "conversational",
    sessionData.coachee_name,
    sessionData.coach_name,
    campaignCodes,
    clientCompanyNames,
    subFunctionalUnitNames
  );

  // AI Coaching pilot region gate: useNudgePopover already folds the region
  // gate into its own aiDisabled (kpiAiDisabled/qaAiDisabled below) and into
  // AiInsightDiv's own visibility, so this stays a plain OR.
  // setIsAiCoachingEnabled (not the read) is still needed here to feed this
  // session's region into that shared state. See alt-ai-coaching-pilot.md.
  const { setIsAiCoachingEnabled } = useCampaign();
  const aiDisabled = kpiAiDisabled || qaAiDisabled;
  const manuallyDisabled = kpiManuallyDisabled || qaManuallyDisabled;

  const handleDisableAI = useCallback(() => {
    handleKpiDisableAI();
    handleQaDisableAI();
  }, [handleKpiDisableAI, handleQaDisableAI]);

  const handleEnableAI = useCallback(() => {
    handleKpiEnableAI();
    handleQaEnableAI();
  }, [handleKpiEnableAI, handleQaEnableAI]);

  const prevAnswerRef = useRef("");
  const qaAnswerAnchorRef = useRef(null);

  const handleFeedbackWithInsight = useCallback(
    type => {
      handleQaFeedback(type);
    },
    [handleQaFeedback]
  );

  const currentPair = useMemo(
    () => questionAnswerPairs[questionNo - 1],
    [questionNo, questionAnswerPairs]
  );

  // function to build the base values for initial form state
  const buildBaseValues = () => ({
    session_id: id,
    isSpecialSession: true,
    isPDFlow: false,
    duration: 0,
    created_by: getLocalValue("uid"),
    grow_form_subject_details: [],
    ...questionFields.reduce((acc, { name }) => {
      acc[name] = name.includes("question") ? t(`growForm.${name}`) : "";
      return acc;
    }, {}),
  });

  // initialValues are controlled here, with enableReinitialize
  const [initialValues, setInitialValues] = useState(() => {
    // seed initial values only once (mount)
    const raw = localStorage.getItem("sessionConversationalFormData");
    const parsed = raw ? safeParse(raw) : null;

    const base = buildBaseValues();

    if (parsed && parsed.session_id === id) {
      return { ...base, ...parsed };
    }
    return base;
  });

  // Formik
  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: values => handleFormSubmit(values),
  });

  const piiScannedRef = useRef({});

  // Known-value lists load async - earlier scans ran against empty arrays,
  // so force a re-scan of every guarded field once they're populated.
  useEffect(() => {
    piiScannedRef.current = {};
  }, [campaignCodes, clientCompanyNames]);

  // Scan answers restored from a saved draft (via enableReinitialize), not
  // just ones the coachee typed and then blurred - onAnswerBlur alone never
  // sees a hydrated value since no blur event fires for it. Question fields
  // are intentionally excluded (see GUARDED_FIELDS).
  useEffect(() => {
    GUARDED_FIELDS.forEach(fieldName => {
      const value = formik.values?.[fieldName];
      if (value?.trim() && piiScannedRef.current[fieldName] !== value) {
        piiScannedRef.current[fieldName] = value;
        piiGuard.checkField(fieldName, value);
      }
    });
  }, [formik.values, campaignCodes, clientCompanyNames, piiGuard]);

  // Check for errors in current question/answer to disable Next/Done button
  const isCurrentQuestionInvalid = Boolean(
    formik.errors[currentPair.answer.name] ||
    formik.errors[currentPair.question.name]
  );

  // persist to localStorage (debounced) when values change
  const debouncedPersist = useDebouncedCallback(values => {
    localStorage.setItem(
      "sessionConversationalFormData",
      JSON.stringify({ ...values, session_id: id })
    );
  }, 300);

  useEffect(() => {
    // only persist if formik is initialized
    if (Object.keys(formik.values).length > 0) {
      debouncedPersist(formik.values);
    }
  }, [formik.values, debouncedPersist, id]);

  useEffect(() => {
    const answers = {};
    for (let i = 1; i <= QUESTION_COUNT; i++) {
      answers[`answer${i}`] = {
        question: `${formik.values[`question${i}`] || ""} ${t(
          `growForm.label${i}`
        )}`.trim(),
        answer: formik.values[`answer${i}`] ?? "",
      };
    }
    sessionAnswersRef.current = answers;
  }, [formik.values, t]);

  const getQaQuestionText = () =>
    `${formik.values[currentPair.question.name] || ""} ${t(
      `growForm.label${questionNo}`
    )}`.trim();

  // fetch session details
  useEffect(() => {
    let mounted = true;
    (async () => {
      setIsLoading(true);
      try {
        const [sessionRes, growFormRes, reviewDateRes] = await Promise.all([
          GetSessionData(id),
          apiRequest.get(`/api/getGrowformDetails?session_id=${id}`),
          apiRequest.get(`/api/getSessionReviewDate?session_id=${id}`),
        ]);

        if (!mounted) return;

        if (sessionRes) {
          setSessionDetails(sessionRes);
          setSessionData(sessionRes.session);
          // AI Coaching pilot region gate: derived from this specific
          // session's campaign -> region, refreshed on every load of this
          // page (bookmark, refresh, or direct nav) so it doesn't depend on
          // CampaignList having run earlier in the browser session. See
          // alt-ai-coaching-pilot.md.
          setIsAiCoachingEnabled(Boolean(sessionRes.ai_coaching_enabled));
        } else {
          addNotification(
            `Could not get session details from id #${id}`,
            "error"
          );
        }

        if (growFormRes.status === 200) {
          const data = growFormRes.data.data;
          const localSubjectList = initialValues.subjects;

          // Data from local storage
          if (Array.isArray(localSubjectList)) {
            setSubjectList(localSubjectList);
          }
          // Data from api
          else if (Array.isArray(data.subject)) {
            setSubjectList(data.subject);
          } else {
            setSubjectList([]);
          }

          setGrowFormDetails(data.grow_form_static_data || null);

          // if we have previous details, we’re in edit mode
          if (data.grow_form_static_data) setIsEditMode(true);

          // if we have previous data, update initial values
          if (data.grow_form_static_data) {
            const updatedFields = questionFields.reduce((acc, f) => {
              const v = data.grow_form_static_data[f.name];
              if (typeof v === "string") acc[f.name] = v;
              return acc;
            }, {});

            setInitialValues(prev => ({
              ...prev,
              ...updatedFields,
              subjects: formik.values.subjects ?? prev.subjects ?? [],
            }));
          }
        } else {
          addNotification(
            `Could not get grow form details from session id #${id} (Status code: ${growFormRes.status})`,
            "error"
          );
        }

        const reviewDate =
          reviewDateRes?.data?.data?.current_review_date?.[0]?.review_datetime;
        if (reviewDate) {
          setSessionReviewDate(dayjs(reviewDate).format("DD MMM YYYY"));
        }
      } catch (err) {
        addNotification(
          "Something went wrong while fetching session data.",
          "error"
        );
      } finally {
        if (mounted) setIsLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [id, addNotification]);

  // keep Formik subjects in sync when subjectList changes
  useEffect(() => {
    if (Array.isArray(subjectList) && subjectList.length > 0) {
      const mapped = subjectList.map(item => ({
        subject_id: item?.subject_id,
        mtd_metric: item?.mtd_metric,
        reality_target: item?.reality_target,
        project_target_kpi: item?.project_target_kpi,
        result_metrics: item?.result_metrics,
        subject: item?.subject,
        sub_subject: item?.sub_subject,
        condition_to_pass: item?.condition_to_pass,
      }));
      formik.setFieldValue("subjects", mapped, false);
      formik.setFieldValue("grow_form_subject_details", mapped, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjectList]);

  // keep duration in form
  useEffect(() => {
    formik.setFieldValue("duration", sessionDuration, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionDuration]);

  const totalQuestions = questionAnswerPairs.length;

  const handleQuestionNavigation = useCallback(
    direction => {
      setQuestionNo(prev => {
        if (direction === "next" && prev < totalQuestions) return prev + 1;
        if (direction === "prev" && prev > 1) return prev - 1;
        return prev;
      });
    },
    [totalQuestions]
  );

  const getTotalTime = time => setSessionDuration(time);

  const resetFormValues = () => {
    const base = buildBaseValues();
    formik.resetForm({ values: base });
    localStorage.removeItem("sessionConversationalFormData");
  };

  const validateFormEntry = async (n, onSuccess) => {
    const errors = await formik.validateForm();
    const questionKey = `question${n}`;
    const answerKey = `answer${n}`;
    const hasErrors = Boolean(errors[questionKey] || errors[answerKey]);
    if (!hasErrors) onSuccess();
    else {
      formik.setFieldTouched(questionKey, true, false);
      formik.setFieldTouched(answerKey, true, false);
    }
  };

  const validateFormSubmit = async onSuccess => {
    // Final validation before submit
    const errors = await formik.validateForm();
    const hasErrors = Object.keys(errors).length > 0;

    if (!hasErrors) onSuccess();
  };

  const handleFormChange = (field, value) => {
    formik.setFieldTouched(field, true, false);
    formik.setFieldValue(field, value, true);
  };

  const handleFormSubmit = async values => {
    setIsLoading(true);

    delete values.subjects; // not needed in payload

    const apiUrl = isEditMode
      ? "/api/editGrowFormDetails"
      : "/api/saveSessionFormDetails";

    const payload = {
      ...values,
      grow_form_subject_details: (values.grow_form_subject_details || []).map(
        s => ({
          subject_id: s.subject_id,
          reality_target: s.reality_target,
        })
      ),
      created_by: getLocalValue("uid"),
    };

    if (isEditMode) {
      payload.duration = {
        duration: sessionDuration,
        session_phase: PDCAStages.DO,
      };
    }

    try {
      const resp = await apiRequest.post(apiUrl, payload);
      if (resp.status === 200) {
        resetFormValues();
        history.push(`/sessions/coaching/summary/${id}`);
      }
    } catch (error) {
      addNotification(`${error}`, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DashboardLayout
      isSessionScoped
      appBarContent={
        <>
          {sessionData && (
            <Stepper
              isPDFlow={false}
              phase={t("header.stepper.do")}
              phaseLabel="Do"
              sessionId={id}
              clockRun={true}
              getTotalTimeSpent={getTotalTime}
            />
          )}
        </>
      }
      rightSidebarContent={
        <NotesSidebar
          sessionID={id}
          isCoach={
            sessionData &&
              Object.prototype.hasOwnProperty.call(sessionData, "coach_id")
              ? sessionData["coach_id"] === parseInt(getLocalValue("uid"), 10)
              : undefined
          }
          accessible={true}
        />
      }
    >
      <div className={styles.pageWrapper}>
        <PageContent indent>
          <Section>
            <>
              <SectionTitle>{t("growForm.startCoaching")}</SectionTitle>
              {manuallyDisabled && (
                <AiBanner
                  aiDisabled
                  onEnable={() => {
                    handleEnableAI();
                    addNotification("AI assist is now on", "success");
                  }}
                />
              )}
              <SessionReminderCard
                plain
                header={t("growForm.yourReviewSessionIsOn")}
                date={sessionReviewDate}
              />
              <div className={styles.sectionLabel}>
                {t("growForm.targetKpi")}
              </div>

              <div className={styles.detailsContainer}>
                <SubjectMetricTargetInput
                  list={
                    subjectList && subjectList.length > 0 ? subjectList : []
                  }
                  setList={setSubjectList}
                  formValidation={formik}
                  hideProjectTargetKpi={false}
                  hideCurrentMetrics={false}
                  hideTargetMetrics={false}
                  isSaveEnabled={true}
                  setHasBeenSaved={setIsMetricEntered}
                  sessionId={id}
                  aiDisabled={aiDisabled}
                  handleDisableAI={handleDisableAI}
                  handleNudgeRequest={handleKpiNudgeRequest}
                  popoverAnchorEl={kpiPopoverAnchorEl}
                  isOpen={kpiIsOpen}
                  streamedText={kpiStreamedText}
                  nudgeType={kpiNudgeType}
                  streaming={kpiStreaming}
                  error={kpiError}
                  handleClosePopover={handleKpiClosePopover}
                  handleFeedback={handleKpiFeedback}
                  handleRefresh={handleKpiRefresh}
                />

                <div className={styles.questionsContainer}>
                  {isMetricEntered && (
                    <>
                      <div className={styles.sectionHeader}>
                        <span className={styles.sectionTitle}>
                          {t("growForm.question")}
                        </span>
                        <div className={styles.navigationPanel}>
                          <KeyboardArrowLeft
                            className={`${styles.navigationButton}
                            ${questionNo > 1 ? "" : styles.hidden}`}
                            onClick={() => handleQuestionNavigation("prev")}
                          />
                          <span className={styles.navigationText}>
                            {`${questionNo} / ${totalQuestions}`}
                          </span>

                          <KeyboardArrowRight
                            className={`${styles.navigationButton}
                              ${questionNo < totalQuestions ? "" : styles.hidden
                              }`}
                            onClick={() =>
                              validateFormEntry(questionNo, () =>
                                handleQuestionNavigation("next")
                              )
                            }
                          />
                        </div>
                      </div>

                      {/* render only the current Q/A pair */}
                      <div
                        key={`row-${questionNo}`}
                        className={styles.questionRow}
                      >
                        <EditableQuestionAnswerForm
                          answerRef={qaAnswerAnchorRef}
                          key={`qa-${questionNo}`}
                          questionFieldName={currentPair.question.name}
                          answerFieldName={currentPair.answer.name}
                          questionValue={
                            formik.values[currentPair.question.name]
                          }
                          answerValue={formik.values[currentPair.answer.name]}
                          formik={formik}
                          onQuestionChange={e =>
                            handleFormChange(
                              currentPair.question.name,
                              e.target.value
                            )
                          }
                          onAnswerChange={e =>
                            handleFormChange(
                              currentPair.answer.name,
                              e.target.value
                            )
                          }
                          onAnswerFocus={() => {
                            prevAnswerRef.current =
                              formik.values[currentPair.answer.name];
                          }}
                          onAnswerBlur={e => {
                            const answerValue =
                              formik.values[currentPair.answer.name];
                            const hits = piiGuard.checkField(
                              currentPair.answer.name,
                              answerValue
                            );
                            if (hits.length > 0) return;
                            if (
                              answerValue?.trim() &&
                              answerValue !== prevAnswerRef.current
                            ) {
                              handleQaNudgeRequest(
                                currentPair.answer.name,
                                getQaQuestionText(),
                                answerValue,
                                qaAnswerAnchorRef.current,
                                [...subjectList],
                                type =>
                                  setQaFeedbackStates(prev => ({
                                    ...prev,
                                    [currentPair.answer.name]: type,
                                  }))
                              );
                              prevAnswerRef.current = answerValue;
                            }
                          }}
                          onManualNudge={e => {
                            const answerValue =
                              formik.values[currentPair.answer.name];
                            const hits = piiGuard.checkField(
                              currentPair.answer.name,
                              answerValue
                            );
                            if (hits.length > 0) return;
                            handleQaNudgeRequest(
                              currentPair.answer.name,
                              getQaQuestionText(),
                              answerValue,
                              qaAnswerAnchorRef.current,
                              [...subjectList],
                              type =>
                                setQaFeedbackStates(prev => ({
                                  ...prev,
                                  [currentPair.answer.name]: type,
                                }))
                            );
                          }}
                          label={t(`growForm.label${questionNo}`)}
                          questionPlaceholder="Type your question here"
                          answerPlaceholder="Type your answer here"
                          questionNo={questionNo}
                          feedbackState={qaFeedbackStates[currentPair.answer.name]}
                          answerPiiBlocked={piiGuard.isBlocked(currentPair.answer.name)}
                          answerPiiHits={piiGuard.getHits(currentPair.answer.name)}
                          onAnswerPiiOverride={(hit, reason) =>
                            piiGuard.overrideHit(currentPair.answer.name, hit, reason)
                          }
                        />

                        <AiPopover
                          open={qaIsOpen && !aiDisabled}
                          anchorEl={qaPopoverAnchorEl}
                          onClose={handleQaClosePopover}
                          nudgeText={qaStreamedText}
                          nudgeType={qaNudgeType}
                          streaming={qaStreaming}
                          error={qaError}
                          onFeedback={handleFeedbackWithInsight}
                          onRefresh={handleQaRefresh}
                          onCloseError={handleQaClosePopover}
                          onDisableAI={handleDisableAI}
                        />
                      </div>

                      <div className={styles.confirmButtonWrapper}>
                        {questionNo === totalQuestions ? (
                          <Button
                            variant="contained"
                            color={"primary"}
                            fullWidth
                            disabled={isCurrentQuestionInvalid || piiGuard.hasAnyBlocking}
                            isloading={isLoading ? 1 : 0}
                            onClick={() =>
                              validateFormSubmit(formik.handleSubmit)
                            }
                          >
                            {t("growForm.done")}
                          </Button>
                        ) : (
                          <Button
                            variant="outlined"
                            color={"primary"}
                            fullWidth
                            disabled={isCurrentQuestionInvalid || piiGuard.hasAnyBlocking}
                            isloading={isLoading ? 1 : 0}
                            onClick={() =>
                              validateFormEntry(questionNo, () => {
                                handleQuestionNavigation("next");
                                handleQaClearNudgeState();
                              })
                            }
                          >
                            {t("growForm.nextQuestion")}
                          </Button>
                        )}
                      </div>

                      <div className={styles.detailsContainer} />
                    </>
                  )}
                </div>
              </div>
            </>
          </Section>
        </PageContent>
      </div>
    </DashboardLayout>
  );
}
